import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import { hasCode } from '@/api/errors'
import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import Pagination from '@/components/ui/Pagination'
import RefreshBar from '@/components/ui/RefreshBar'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import { depthKeys } from '@/features/depth/api/keys'
import { useDepthSectors, useMarketDepth } from '@/features/depth/api/queries'
import DepthFilters from '@/features/depth/components/DepthFilters'
import DepthGrid from '@/features/depth/components/DepthGrid'
import MarketClosedState from '@/features/market/components/MarketClosedState'
import { useLastClose } from '@/features/market/lib/useLastClose'
import { useManualRefresh } from '@/hooks/useManualRefresh'
import { useMarketStatus } from '@/hooks/useMarketStatus'
import { useOnline } from '@/hooks/useOnline'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import { depthSearchSchema, loadDepth } from '@/screens/market/DepthScreen.loader'
import type { DepthPage } from '@/schemas/depth'

const noop = () => undefined

/** The mock stacks the strip, the filters and the grid 16 apart, not the page's 24. */
const STACK = 'gap-4'

const HEADER = <TitleRow title="Market Depth" />

/**
 * The loaded page, market open: the one filters row (44 below `lg`, 36 from it),
 * then the order-book grid at `DepthGrid`'s own loaded shape — 2-up cards — at
 * the page's own default `pageSize` of 10.
 *
 * Drawn as a guess, not a certainty: `DepthScreen.loader.ts` cannot know
 * before it resolves whether the market is open or closed, so this is the
 * OPEN shape — the common case, and the one whose data the loader is
 * actually fetching while this shows (it awaits the books, not just the
 * sector list, once status says open — see that loader's own doc comment).
 * A market that turns out closed never hands off to this grid at all: the
 * loader resolves status before it ever reaches the books, so by the time
 * the real screen mounts, `showClosed` is already known and it renders
 * `MarketClosedState` directly — the jump this page used to have, from this
 * same grid shape to that state's much shorter copy, is gone because there
 * is no longer a beat where the screen has mounted without knowing which one
 * it is.
 */
function DepthPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading market depth…"
      header={HEADER}
      pageTitle="Market Depth"
      mainClassName={STACK}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Skeleton className="h-11 w-full rounded-sm @sm:w-55 lg:h-9" />
        <Skeleton className="h-11 w-full flex-1 rounded-sm @sm:min-w-60 lg:h-9" />
        <Skeleton className="h-11 w-18 rounded-sm lg:h-9" />
      </div>
      <div className="grid gap-4 @lg:grid-cols-2 @lg:gap-x-5">
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={index} className="h-90 w-full" />
        ))}
      </div>
    </ScreenLayout>
  )
}

function DepthError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={HEADER}
      title="Couldn't load market depth"
      description="The data did not come back. Nothing on your device is wrong."
    />
  )
}

export const Route = createFileRoute('/_app/market/depth')({
  validateSearch: depthSearchSchema,
  loader: loadDepth,
  pendingComponent: DepthPending,
  errorComponent: DepthError,
  component: DepthScreen,
})

function DepthScreen() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const symbols = search.symbols ?? []
  const status = useMarketStatus()
  const online = useOnline()
  const query = useMarketDepth({ ...search, symbols })
  const manual = useManualRefresh([query])
  const sectors = useDepthSectors()
  // `depthKeys.books()` is the whole board's prefix, not one filter's own key —
  // a sector/symbol change the member has never seen succeed can still recover
  // whatever book last loaded under a DIFFERENT filter this session (`5327:4326`,
  // "Show last close").
  const lastClose = useLastClose<DepthPage>(depthKeys.books(), query.data)
  const data = lastClose.data

  // A race at the close: the status query's own poll has not caught up yet,
  // so `useMarketDepth` was still `enabled` when mm-be flipped to closed —
  // the request it had in flight lands as this 409 instead of a body. Same
  // closed UI as the gate catching it up front, branched on the wire code
  // and never the message (api/errors.ts's whole contract).
  const closedMidFlight = query.isError && hasCode(query.error, 'MARKET_CLOSED')
  const showClosed = !status.isPending && (!status.isOpen || closedMidFlight)
  // The gate above is checked FIRST, same as before: a disabled query with no
  // data reads as `loading`, which would otherwise show a page skeleton for a
  // fact that isn't loading at all — see W1-S-states.md's own "disabled
  // query" edge case, which names this screen as the example. A recovered
  // last-close copy reads as `loaded` outright, ahead of the query's own
  // (offline/paused) state.
  const view = lastClose.isShowingSaved ? 'loaded' : resolveViewState(query, { online })

  return (
    <ScreenLayout header={HEADER} pageTitle="Market Depth" mainClassName={STACK}>
      <DepthFilters
        sectors={sectors.data ?? []}
        sector={search.sector}
        symbols={symbols}
        onApply={(next) => {
          void navigate({
            search: (prev) => ({
              ...prev,
              sector: next.sector,
              // No selection is the absence of a filter, not a value, so it
              // leaves the URL entirely and a shared link carries only what
              // the member actually narrowed to.
              symbols: next.symbols.length > 0 ? next.symbols : undefined,
              page: 1,
            }),
          })
        }}
      />

      {showClosed ? (
        <MarketClosedState nextOpen={status.nextOpen} />
      ) : view === 'failed' || view === 'offline' ? (
        <EmptyState
          kind={view}
          size="page"
          title={view === 'offline' ? undefined : "Couldn't load market depth"}
          onRetry={manual.refresh}
          retrying={manual.isManual}
          meta={view === 'failed' ? lastUpdatedMeta(query.dataUpdatedAt) : undefined}
          savedAt={lastClose.savedAt}
          onShowLastClose={lastClose.showLastClose}
        />
      ) : (
        <>
          {lastClose.isShowingSaved ? (
            <RefreshBar state="offline" onRetry={manual.refresh} />
          ) : null}

          <DepthGrid
            books={data?.rows ?? []}
            // While the status itself is still resolving, `useMarketDepth`
            // has not fired yet either — `query.isLoading` reads false for a
            // disabled query, so the skeleton would drop for a beat between
            // "status pending" and "status open, depth now fetching". Folding
            // `status.isPending` in keeps it one continuous loading state.
            // Never true once a saved copy is on screen — there is already
            // something real to show, not a gap to fill with bars.
            isLoading={!lastClose.isShowingSaved && (status.isPending || query.isLoading)}
            pageSize={search.pageSize}
          />

          {/* The count caption and the pager are this table's foot and travel with
              it: `Pagination` draws its own 24px (see its PLACEMENT note) and the
              screen stack's `gap-6` would add to it. */}
          <div>
            <Pagination
              page={data?.page ?? search.page}
              pageSize={data?.pageSize ?? search.pageSize}
              total={data?.total ?? 0}
              unit="companies"
              // The mock's foot: the count where "Rows per page" sits, the pager at the other end,
              // and a fixed page of six. Both figures are the page's own, so a sector with nineteen
              // names says so. Every name starts selected, so "selected" is always true.
              lead={
                <p className="text-body-xs text-muted-foreground-subtle">
                  Showing {data?.rows.length ?? symbols.length} of {data?.total ?? symbols.length}{' '}
                  selected companies
                </p>
              }
              onPageChange={(page) => {
                void navigate({ search: (prev) => ({ ...prev, page }) })
              }}
              onPageSizeChange={noop}
            />
          </div>
        </>
      )}
    </ScreenLayout>
  )
}
